'use client'

import { useEffect } from 'react'

import { captureException } from '@/lib/observability'

// Last-resort error boundary. Triggers when the root layout itself
// throws, in which case `error.tsx` cannot run because it lives below
// the layout that crashed. Must include <html> + <body> tags because
// it fully replaces the layout. Style is deliberately inline-only:
// the CSS bundle from layout.tsx is not loaded in this code path.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    if (typeof console !== 'undefined') {
      console.error('Root-layout error:', error, error.digest)
    }
    captureException(error, {
      digest: error.digest,
      source: 'app/global-error.tsx',
    })
  }, [error])

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          background: '#fef4e3',
          color: '#0b0c10',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4rem 1.5rem',
        }}
      >
        <main style={{ maxWidth: 28 * 16, width: '100%' }}>
          <div
            style={{
              borderRadius: 24,
              border: '1px solid rgba(94, 31, 41, 0.30)',
              background: '#fbf7ec',
              padding: '2rem 2.5rem',
            }}
          >
            <h1
              style={{
                fontSize: 24,
                fontWeight: 600,
                margin: 0,
                letterSpacing: '-0.02em',
              }}
            >
              PulseSignal is having trouble loading.
            </h1>
            <p
              style={{
                marginTop: 12,
                color: 'rgba(11, 12, 16, 0.7)',
                lineHeight: 1.6,
              }}
            >
              A low-level error stopped the page from rendering. Refreshing
              usually fixes it. If it keeps happening, email{' '}
              <a
                href="mailto:hello@pulsesignal.co"
                style={{ color: '#7e3818', textDecoration: 'underline' }}
              >
                hello@pulsesignal.co
              </a>
              .
            </p>
            {error.digest && (
              <p
                style={{
                  marginTop: 12,
                  color: 'rgba(11, 12, 16, 0.55)',
                  fontFamily:
                    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                  fontSize: 13,
                }}
              >
                Reference: {error.digest}
              </p>
            )}
            <div style={{ marginTop: 28 }}>
              <button
                type="button"
                onClick={() => reset()}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 20px',
                  borderRadius: 9999,
                  border: 'none',
                  background: '#a44a20',
                  color: '#fbf7ec',
                  fontWeight: 500,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                Try again
              </button>
            </div>
          </div>
        </main>
      </body>
    </html>
  )
}
